import React, { useState, useMemo } from 'react';
import { OrderTypeSection } from './OrderTypeSection';
import { useHandleFormData } from '../hooks/useHandleFormData';
import { AddPublicOrderDetails } from '../AddPublicOrderDetails';
import OrderDetails from './OrderDetails';
import { ENDPOINTS, SUBMIT_STATUS as STATUS } from '../../../helpers/constants';
import { Completion } from './Completion';
import { makeRESTApiRequest } from '../../../helpers/api';
import { toast } from 'sonner';
import { isStaff } from '../../../helpers/global';
import { appendHoaSlotFiles, serializeClientRequestHoaInfo, serializeHomeOwnerInformation } from '../../hoaForm/hoaFormData';

const buildCreateServiceFormData = payload => {
  const { supportingDocuments = [], orders = [], ...serviceData } = payload;
  const formData = new FormData();

  const processedOrders = orders.map(order => {
    if (order.orderType === 'Estoppel' && Array.isArray(order.requestHoaInfo)) {
      return order;
    }
    if (order.orderType === 'Estoppel' && Array.isArray(order.homeOwnerInformation)) {
      appendHoaSlotFiles(formData, order.homeOwnerInformation);
      return {
        ...order,
        homeOwnerInformation: serializeHomeOwnerInformation(order.homeOwnerInformation),
      };
    }
    return order;
  });

  formData.append('data', JSON.stringify({ ...serviceData, orders: processedOrders }));

  supportingDocuments.forEach(({ file }) => {
    if (file instanceof File) {
      formData.append('supportingDocuments', file, file.name);
    }
  });

  return formData;
};

export const FormSection = ({ currentStep, setCurrentStep, isInternal, onServiceCreate, onClose }) => {
  const [status, setStatus] = useState(STATUS.IDLE);
  const [orderDetails, setOrderDetails] = useState({});
  const [confirmationEmails, setConfirmationEmails] = useState([]);

  const role = useMemo(() => localStorage.getItem('user-role'), []);

  const handleForward = () => {
    if (currentStep >= 2) return;
    if (currentStep === 1 && isInternal) return;
    setCurrentStep(currentStep => currentStep + 1);
  };
  const handleBack = () => {
    if (currentStep === 0) return;
    setCurrentStep(currentStep => currentStep - 1);
  };
  const { userDetails, orderTypes, setOrderType } = useHandleFormData();

  const handleOrderDetailsSubmit = formData => {
    setOrderDetails(formData);
    if (isInternal) {
      handleFinalSubmit(ENDPOINTS.CREATE_SERVICE, formData);
    }
    handleForward();
  };

  const handleFinalSubmit = async (endpoint, formData = {}) => {
    setStatus(STATUS.LOADING);
    const mergedPayload = { ...orderDetails, ...formData };

    try {
      const { error, response } = await makeRESTApiRequest({
        endpoint,
        method: 'POST',
        contentType: 'multipart/form-data',
        requestBody: buildCreateServiceFormData(mergedPayload),
        stringify: false
      });
      if (error) {
        throw error;
      }

      const uploadErrors = response?.supportingDocumentsUploadErrors;
      if (uploadErrors?.length) {
        const message =
          uploadErrors.length === 1
            ? `Order created, but a document failed to upload: ${uploadErrors[0].name || ''} ${uploadErrors[0].message || ''}`.trim()
            : `Order created, but ${uploadErrors.length} document(s) failed to upload.`;
        toast.error(message, { duration: 5000 });
      }

      setConfirmationEmails(response.clientEmailsNotified);
      setStatus(STATUS.SUCCESS);
      onServiceCreate && onServiceCreate(response);
    } catch (error) {
      toast.error(error, { duration: 4000 });
      setStatus(STATUS.FAILED);
    }
  };

  if (status === STATUS.SUCCESS) {
    if (isStaff(role)) {
      toast.success('Order Successfully Submitted!');
      onClose();
      return;
    }

    return (
      <div className="form-section-container">
        <Completion isInternal={isInternal} confirmationEmails={confirmationEmails} onClose={onClose} />
      </div>
    );
  }

  switch (currentStep) {
    case 0:
      return (
        <div className="form-section-container">
          <OrderTypeSection
            handleBack={handleBack}
            handleForward={handleForward}
            defaultActiveOrderType={orderTypes}
            setOrderType={setOrderType}
          />
        </div>
      );
    case 1:
      return (
        <div className="form-section-container" style={{ padding: 0 }}>
          <OrderDetails
            handleBack={handleBack}
            handleForward={handleForward}
            defaultActiveOrderType={orderTypes}
            setOrderType={setOrderType}
            isInternal={isInternal}
            status={status}
            handleOrderDetailsSubmit={handleOrderDetailsSubmit}
            orderDetails={orderDetails}
          />
        </div>
      );
    case 2:
      return (
        <div className="form-section-container">
          <AddPublicOrderDetails
            defaultValues={userDetails}
            handleBack={handleBack}
            handleForward={handleForward}
            handleFinalSubmit={handleFinalSubmit}
            status={status}
          />
        </div>
      );
    default:
      return null;
  }
};
